import { useNavigate } from "react-router-dom";
import { Sparkles, Check, ArrowRight, Calendar, Tag, Bell, BookOpen, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlans } from "@/lib/usePlans";

/**
 * YearlyPerksPage
 *
 * Marketing-style upsell that walks a paying-monthly (or trialing)
 * customer through the upside of switching to the yearly plan. CTA
 * deep-links into the existing /app/upgrade?plan=yearly flow so the
 * actual Stripe Checkout reuse path is unchanged.
 *
 * v77.99.21 — Pricing pulled from the backend `/billing/plans`
 * endpoint via the shared `usePlans` hook. The page renders the
 * "Switch to yearly" CTA optimistically using sensible fallbacks
 * (€10.99 / €99) so the layout doesn't jump while the hook is
 * fetching; once the live numbers arrive the strings update.
 */
const FALLBACK = {
  currency_symbol: "€",
  monthly: { price: 10.99, price_per_year: 131.88 },
  yearly: {
    price: 99,
    effective_monthly: 8.25,
    savings_vs_monthly: 32.88,
    months_free_equivalent: 3,
  },
};

const fmt = (n) => Number(n).toFixed(2);
const fmtInt = (n) => String(Math.round(Number(n)));

const TONE = {
  teal:   { bg: "bg-[#D6F0EF]", text: "text-[#116B6E]", border: "border-[#A7D8DA]" },
  violet: { bg: "bg-[#ECE3F3]", text: "text-[#4E3877]", border: "border-[#D5C5E5]" },
  amber:  { bg: "bg-[#FDF4E2]", text: "text-[#8F6B1F]", border: "border-[#E8D6A4]" },
};

export default function YearlyPerksPage() {
  const navigate = useNavigate();
  const { plans } = usePlans();
  const p = plans || FALLBACK;
  const sym = p.currency_symbol || "€";
  const monthly = p.monthly.price;
  const yearly = p.yearly.price;
  const savings = p.yearly.savings_vs_monthly;
  const monthsFree = p.yearly.months_free_equivalent;
  const effectiveMonthly = p.yearly.effective_monthly;
  const fullAnnualMonthly = p.monthly.price_per_year;

  const PERKS = [
    {
      icon: Tag,
      title: `Save ${sym}${fmt(savings)}/year`,
      body: `That's effectively ${fmtInt(monthsFree)} month${monthsFree === 1 ? "" : "s"} free vs paying monthly. Billed once, no surprises.`,
      tone: "teal",
    },
    {
      icon: Calendar,
      title: "Yearly-locked court bundle archive",
      body: "Twelve months of receipts, messages, journal entries and audit logs ready to export as a single dated PDF whenever your solicitor asks.",
      tone: "violet",
    },
    {
      icon: Bell,
      title: "Priority support response (under 24h)",
      body: "Yearly subscribers jump the queue. Real human reply by next business day for anything billing, exports, or co-parent invites.",
      tone: "amber",
    },
    {
      icon: BookOpen,
      title: "Annual co-parenting summary report",
      body: "An automatically-generated PDF every January summarising your year: messages exchanged, requests agreed, settlements logged. Great for your records and your therapist if you have one.",
      tone: "teal",
    },
    {
      icon: ShieldCheck,
      title: "Lock in today's price for a full year",
      body: "Insulated from any pricing changes for 12 months — even if monthly rates go up.",
      tone: "violet",
    },
  ];

  const goUpgrade = () => navigate("/app/upgrade?plan=yearly&utm_source=yearly_perks");

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-8" data-testid="yearly-perks-page">
      {/* Hero */}
      <header className="text-center space-y-3">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FDF4E2] text-[#8F6B1F] border border-[#E8D6A4] rounded-full text-[11px] uppercase tracking-[0.1em]">
          <Sparkles className="w-3.5 h-3.5" /> Yearly plan
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A2225]" data-testid="yearly-perks-headline">
          Pay yearly. Save {sym}{fmt(savings)}.<br className="hidden sm:block" /> Sleep easier.
        </h1>
        <p className="text-base sm:text-lg text-[#6F828A] max-w-2xl mx-auto">
          One annual payment of {sym}{fmtInt(yearly)} unlocks every feature for a full year — and a few perks that monthly subscribers don&apos;t get.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap pt-3">
          <Button
            onClick={goUpgrade}
            className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-11 px-6 text-base"
            data-testid="yearly-perks-cta"
          >
            Switch to yearly <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <button
            type="button"
            onClick={() => navigate("/app/upgrade")}
            className="text-sm text-[#6F828A] hover:text-[#2E3A3F] underline-offset-2 hover:underline"
            data-testid="yearly-perks-cta-monthly"
          >
            Stick with monthly →
          </button>
        </div>
      </header>

      {/* Price comparison */}
      <section className="grid sm:grid-cols-2 gap-4" data-testid="yearly-perks-comparison">
        <div className="border border-[#D9E1E3] bg-white rounded-md p-5">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Monthly</div>
          <div className="font-heading text-3xl font-semibold mt-1 text-[#2E3A3F] tabular-nums">
            {sym}{fmt(monthly)}<span className="text-sm font-normal text-[#6F828A]">/month</span>
          </div>
          <div className="text-sm text-[#6F828A] mt-2">
            {sym}{fmt(fullAnnualMonthly)} per year · cancel anytime.
          </div>
        </div>
        <div className="border-2 border-[#1F9FA4] bg-[#F4FAFA] rounded-md p-5 relative">
          <span className="absolute -top-2 right-4 px-2 py-0.5 bg-[#1F9FA4] text-white text-[10px] uppercase tracking-[0.1em] rounded-sm">
            Best value
          </span>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#116B6E]">Yearly</div>
          <div className="font-heading text-3xl font-semibold mt-1 text-[#0B4D50] tabular-nums">
            {sym}{fmtInt(yearly)}<span className="text-sm font-normal text-[#6F828A]">/year</span>
          </div>
          <div className="text-sm text-[#116B6E] mt-2 font-medium">
            ~ {sym}{fmt(effectiveMonthly)}/month effective · save {sym}{fmt(savings)}.
          </div>
        </div>
      </section>

      {/* Perks list */}
      <section className="space-y-4" data-testid="yearly-perks-list">
        <h2 className="font-heading text-2xl font-semibold text-[#1A2225]">
          What you get with yearly
        </h2>
        <ul className="space-y-3">
          {PERKS.map((perk, i) => {
            const tone = TONE[perk.tone] || TONE.teal;
            const Icon = perk.icon;
            return (
              <li
                key={perk.title || `perk-${i}`}
                className="flex items-start gap-4 p-4 border border-[#D9E1E3] bg-white rounded-md"
                data-testid={`yearly-perk-${i}`}
              >
                <span
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-sm border ${tone.bg} ${tone.text} ${tone.border} flex-shrink-0`}
                >
                  <Icon className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base font-semibold text-[#2E3A3F]">
                    {perk.title}
                  </div>
                  <div className="text-sm text-[#6F828A] mt-0.5">{perk.body}</div>
                </div>
                <Check className="w-5 h-5 text-[#1F9FA4] flex-shrink-0 hidden sm:block" />
              </li>
            );
          })}
        </ul>
      </section>

      {/* Bottom CTA */}
      <section
        className="border border-[#1F9FA4] bg-gradient-to-br from-[#F4FAFA] to-[#E0F2F2] rounded-md p-6 sm:p-8 text-center space-y-3"
        data-testid="yearly-perks-bottom-cta"
      >
        <h3 className="font-heading text-2xl font-semibold text-[#0B4D50]">
          Ready to switch?
        </h3>
        <p className="text-sm text-[#116B6E] max-w-md mx-auto">
          You&apos;ll be redirected to a secure Stripe Checkout. Your monthly subscription is automatically pro-rated against the yearly switch.
        </p>
        <Button
          onClick={goUpgrade}
          className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-11 px-6 text-base"
          data-testid="yearly-perks-cta-bottom"
        >
          Switch to yearly for {sym}{fmtInt(yearly)} <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
        <p className="text-[11px] text-[#6F828A]">
          Cancel anytime · 7-day money-back guarantee · supports & UK or Ireland VAT
        </p>
      </section>
    </div>
  );
}
